import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ClipboardList, CalendarDays, FolderOpen, Vote, Download, CheckCircle2, MapPin, ShieldCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import FormRenderer from "@/components/FormRenderer";
import { RichContent } from "@/components/RichTextEditor";

export default function ParentCourse({ course }) {
  const cid = course.id;
  const [regs, setRegs] = useState([]);
  const [termine, setTermine] = useState([]);
  const [files, setFiles] = useState([]);
  const [polls, setPolls] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dsTemplates, setDsTemplates] = useState([]);
  const [askMore, setAskMore] = useState(false);
  const [resetFn, setResetFn] = useState(null);
  const PSB = { psb1: "Personensorgeberechtigte 1 (Mutter)", psb2: "Personensorgeberechtigte 2 (Vater)" };

  const loadAll = () => {
    api.get(`/courses/${cid}/registrations`).then(({ data }) => setRegs(data)).catch(() => {});
    api.get(`/courses/${cid}/termine`).then(({ data }) => setTermine(data)).catch(() => {});
    api.get(`/courses/${cid}/files`).then(({ data }) => setFiles(data)).catch(() => {});
    api.get(`/courses/${cid}/polls`).then(({ data }) => setPolls(data)).catch(() => {});
    api.get(`/datenschutz-templates`).then(({ data }) => setDsTemplates(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [cid]);

  const submitReg = async (payload, reset) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/courses/${cid}/registrations`, { ...payload, mode: "gemeinsam" });
      reset(); loadAll();
      toast.success("Anmeldung gespeichert.");
      window.open(`${API}/registrations/${data.id}/pdf?token=${getToken()}`, "_blank");
      setResetFn(() => reset); setAskMore(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const hasReg = regs.length > 0;
  const hasDs = regs.some((r) => r.datenschutz && r.datenschutz.accepted);
  const dsRegs = regs.filter((r) => r.datenschutz && r.datenschutz.accepted);

  return (
    <div className="px-4 sm:px-8 py-8 max-w-4xl">
      <h1 className="text-3xl font-serif mb-1">{course.kurs_name}</h1>
      <p className="text-muted-foreground text-sm mb-6 flex items-center gap-1"><MapPin size={14} /> {course.kurs_gemeinde}</p>

      <Tabs defaultValue="anmeldung">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="anmeldung" data-testid="ptab-anmeldung"><ClipboardList size={15} className="mr-1" /> Anmeldung{hasReg && <CheckCircle2 size={14} className="ml-1 text-green-600" data-testid="ptab-anmeldung-done" />}</TabsTrigger>
          <TabsTrigger value="datenschutz" data-testid="ptab-datenschutz"><ShieldCheck size={15} className="mr-1" /> Datenschutzerklärung{hasDs && <CheckCircle2 size={14} className="ml-1 text-green-600" data-testid="ptab-datenschutz-done" />}</TabsTrigger>
          <TabsTrigger value="beschreibung" data-testid="ptab-beschreibung"><FileText size={15} className="mr-1" /> Informationen</TabsTrigger>
          <TabsTrigger value="dateien" data-testid="ptab-dateien"><FolderOpen size={15} className="mr-1" /> Dateiablage</TabsTrigger>
          <TabsTrigger value="termine" data-testid="ptab-termine"><CalendarDays size={15} className="mr-1" /> Termine</TabsTrigger>
          <TabsTrigger value="umfrage" data-testid="ptab-umfrage"><Vote size={15} className="mr-1" /> Terminumfrage</TabsTrigger>
        </TabsList>

        <TabsContent value="beschreibung" className="pt-6">
          <div className="bg-white border border-border rounded-lg shadow-sm p-6">
            {course.beschreibung ? <RichContent html={course.beschreibung} className="text-slate-700" /> : <p className="text-muted-foreground text-sm">Für diesen Kurs wurde noch keine Beschreibung hinterlegt.</p>}
          </div>
        </TabsContent>

        <TabsContent value="anmeldung" className="pt-6">
          {regs.length > 0 && (
            <div className="bg-white border border-border rounded-lg shadow-sm p-5 mb-6">
              <h3 className="font-semibold mb-3">Ihre bisherigen Anmeldungen</h3>
              <div className="space-y-2">
                {regs.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`my-reg-${r.id}`}>
                    <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-green-600" /> {r.kind_name || "Anmeldung"}</span>
                    <a href={`${API}/registrations/${r.id}/pdf?token=${getToken()}`} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1"><Download size={14} /> PDF</a>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(() => {
            const formFields = course.form_fields_gemeinsam || course.form_fields || [];
            return (
              <div className="bg-white border border-border rounded-lg shadow-sm p-6">
                <h3 className="font-semibold mb-2">Anmeldung</h3>
                <p className="text-sm text-muted-foreground mb-4">Für die Anmeldung ist die Zustimmung beider Personensorgeberechtigten notwendig.</p>
                {formFields.length === 0
                  ? <p className="text-muted-foreground text-sm">Das Anmeldeformular wurde noch nicht bereitgestellt.</p>
                  : <FormRenderer courseId={cid} fields={formFields} onSubmit={submitReg} submitting={submitting} dsTemplates={dsTemplates} consents={[{ key: "psb1", label: PSB.psb1 }, { key: "psb2", label: PSB.psb2 }]} submitLabel="Anmeldung absenden" />}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="termine" className="pt-6">
          <div className="space-y-3">
            {termine.map((t) => (
              <div key={t.id} className="bg-white border border-border rounded-lg shadow-sm p-4" data-testid={`ptermin-${t.id}`}>
                <p className="font-medium">{t.titel}</p>
                <p className="text-sm text-muted-foreground">{new Date(t.datum).toLocaleDateString("de-DE")}{t.uhrzeit && ` · ${t.uhrzeit}`}</p>
                {t.beschreibung && <RichContent html={t.beschreibung} className="text-sm mt-1" />}
              </div>
            ))}
            {termine.length === 0 && <p className="text-muted-foreground text-sm">Keine Termine vorhanden.</p>}
          </div>
        </TabsContent>

        <TabsContent value="dateien" className="pt-6">
          <div className="space-y-2">
            {files.map((f) => (
              <div key={f.id} className="bg-white border border-border rounded-lg shadow-sm p-4 flex items-center justify-between" data-testid={`pfile-${f.id}`}>
                <p className="font-medium truncate">{f.original_filename}</p>
                <a href={`${API}/files/${f.id}/download?token=${getToken()}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Download size={15} className="mr-1" /> Download</Button></a>
              </div>
            ))}
            {files.length === 0 && <p className="text-muted-foreground text-sm">Keine Dateien vorhanden.</p>}
          </div>
        </TabsContent>

        <TabsContent value="umfrage" className="pt-6">
          {polls.length > 0
            ? <div className="space-y-6">{polls.map((p) => <ParentPoll key={p.id} poll={p} reload={loadAll} />)}</div>
            : <p className="text-muted-foreground text-sm">Zurzeit gibt es keine Terminumfrage.</p>}
        </TabsContent>

        <TabsContent value="datenschutz" className="pt-6">
          {dsRegs.length === 0 ? (
            <p className="text-muted-foreground text-sm">Die Datenschutzerklärung wird bei der Anmeldung erzeugt und steht nach dem Absenden hier als PDF zum Download bereit.</p>
          ) : (
            <div className="space-y-2">
              {dsRegs.map((r) => (
                <div key={r.id} className="bg-white border border-border rounded-lg shadow-sm p-4 flex items-center justify-between" data-testid={`ds-file-${r.id}`}>
                  <div>
                    <p className="font-medium flex items-center gap-2"><CheckCircle2 size={16} className="text-green-600" /> {r.kind_name || "Anmeldung"}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Bestätigt am {new Date(r.datenschutz.timestamp).toLocaleString("de-DE")}{r.datenschutz.name ? ` · ${r.datenschutz.name}` : ""}</p>
                  </div>
                  <a href={`${API}/registrations/${r.id}/datenschutz-pdf?token=${getToken()}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid={`ds-download-${r.id}`}><Download size={15} className="mr-1" /> PDF</Button></a>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={askMore} onOpenChange={setAskMore}>
        <DialogContent>
          <DialogHeader><DialogTitle>Weitere Person anmelden?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Ihre Anmeldung wurde gespeichert und das PDF geöffnet. Möchten Sie eine weitere Person anmelden?</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAskMore(false)} data-testid="no-more-button">Nein, fertig</Button>
            <Button onClick={() => setAskMore(false)} data-testid="yes-more-button">Ja, weitere Person</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ParentPoll({ poll, reload }) {
  const [name, setName] = useState("");
  const [answers, setAnswers] = useState({});
  const set = (oid, v) => setAnswers((a) => ({ ...a, [oid]: v }));
  const submit = async () => {
    if (!name) return toast.error("Bitte Namen angeben.");
    try {
      await api.post(`/polls/${poll.id}/respond`, { name, antworten: answers });
      toast.success("Rückmeldung gespeichert."); setName(""); setAnswers({}); reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="bg-white border border-border rounded-lg shadow-sm p-5" data-testid={`ppoll-${poll.id}`}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold">{poll.titel}</h3>
        {poll.closed && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">geschlossen</span>}
      </div>
      {poll.frist && <p className="text-xs text-muted-foreground mb-3">Rückmeldung bis {new Date(poll.frist).toLocaleDateString("de-DE")}</p>}
      {poll.beschreibung && <RichContent html={poll.beschreibung} className="text-sm mb-3" />}
      {poll.closed ? (
        <p className="text-sm text-muted-foreground" data-testid={`ppoll-closed-${poll.id}`}>Diese Terminumfrage ist geschlossen. Eine Rückmeldung ist nicht mehr möglich.</p>
      ) : (
        <>
          <Input placeholder="Ihr Name / Name des Kindes" value={name} onChange={(e) => setName(e.target.value)} className="mb-4 max-w-sm" data-testid={`ppoll-name-${poll.id}`} />
          <div className="space-y-2">
            {poll.optionen.map((o) => (
              <div key={o.id} className="flex items-center justify-between border border-border rounded-md px-4 py-2">
                <span className="text-sm">{new Date(o.datum).toLocaleDateString("de-DE")}{o.uhrzeit && ` · ${o.uhrzeit}`}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant={answers[o.id] === "ja" ? "default" : "outline"} onClick={() => set(o.id, "ja")} data-testid={`ppoll-yes-${o.id}`}>Kann teilnehmen</Button>
                  <Button size="sm" variant={answers[o.id] === "nein" ? "destructive" : "outline"} onClick={() => set(o.id, "nein")} data-testid={`ppoll-no-${o.id}`}>Kann nicht</Button>
                </div>
              </div>
            ))}
          </div>
          <Button className="mt-4" onClick={submit} data-testid={`ppoll-submit-${poll.id}`}>Rückmeldung senden</Button>
        </>
      )}
    </div>
  );
}
